import CircleIcon from '@mui/icons-material/Circle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Button, Popover, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { GroupSettingsContext } from "../../../contexts/GroupSettingsContext";
import { updateDashboardFunc } from "../../../lib/helper";
import theme from "../../../theme";
import { ColorOptions } from "../../../theme/colors";
import { Dashboard } from "../../../types/files";
import moment from 'moment';

interface StatusHistoryItem {
    status: string;
    timestamp: string;
}

const useStyles = makeStyles(() => ({
    statusButton: {
        height: '30px',
        width: 'auto',
        padding: '0 12px',
        borderRadius: '24px',
        transition: 'all 0.2s ease',
        userSelect: 'none',
    },
    statusText: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: '30px',
        width: 'auto',
        padding: '0 4px',
        borderRadius: 24,
        fontFamily: 'Inter',
        fontSize: '0.8rem',
        fontWeight: '500',
        textTransform: 'capitalize',
    },
    popoverPaper: {
        borderRadius: '12px',
        boxShadow: '0px 4px 6px -2px rgba(0, 0, 0, 0.05), 0px 10px 15px -3px rgba(0, 0, 0, 0.1)',
        marginTop: '4px',
        overflow: 'visible',
    },
    leftSection: {
        flex: 1,
        padding: '12px',
        borderRight: '1px solid #E5E7EB',
    },
    rightSection: {
        width: '200px',
        height: '100%',
        padding: '12px',
    },
    sectionTitle: {
        fontFamily: 'Inter, -apple-system, sans-serif',
        fontSize: '11px',
        fontWeight: 600,
        color: '#9CA3AF',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '8px',
        paddingLeft: '8px',
    },
    statusOption: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '6px 16px',
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
        '&:hover': {
            backgroundColor: '#F3F4F6',
        },
    },
    statusOptionLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    statusOptionText: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: '30px',
        width: 'auto',
        padding: '0 4px',
        borderRadius: 24,
        fontFamily: 'Inter',
        fontSize: '0.8rem',
        fontWeight: '500',
        textTransform: 'capitalize',
    },
    currentBadge: {
        fontSize: '11px',
        fontWeight: 600,
        color: '#0891B2',
        textTransform: 'capitalize',
        letterSpacing: '0.5px',
    },
    historyItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        padding: '8px',
        marginBottom: '4px',
    },
    historyContent: {
        flex: 1,
    },
    historyStatus: {
        fontFamily: 'Inter',
        fontSize: '0.8rem',
        fontWeight: 600,
        color: '#1F2937',
        textTransform: 'capitalize',
        lineHeight: '18px',
        marginBottom: '2px',
    },
    historyTimestamp: {
        fontFamily: 'Inter, -apple-system, sans-serif',
        fontSize: '12px',
        fontWeight: 400,
        color: '#6B7280',
        lineHeight: '16px',
    },
}));

const DashboardStatusMenu: FC<{
    dashboard: Dashboard,
    withTitle?: boolean,
    readOnly?: boolean,
    statusHistory?: StatusHistoryItem[],
}> = ({ dashboard, withTitle, readOnly, statusHistory = [] }) => {
    const classes = useStyles();
    const { statuses } = useContext(GroupSettingsContext);
    const [selected, setSelected] = useState<string>('');
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const palettes = useMemo(() => Array.from(new Set(Object.values(ColorOptions)
        .filter(color => !['neutral', 'brown', 'lime', 'pink', 'red', 'shades'].includes(color)))), []);

    const getColors = useCallback((title: string, withBorder?: boolean) => {
        const colorMap: Record<string, { bg: string; text: string; border: string }> = {
            'New':   { bg: '#F0FDFA', text: '#0D9488', border: '#99F6E4' }, // teal-50 / teal-600 / teal-200
            'Lead':  { bg: '#EFF6FF', text: '#2563EB', border: '#BFDBFE' }, // blue-50 / blue-600 / blue-200
            'Tier 1': { bg: '#FFF7ED', text: '#EA580C', border: '#FED7AA' }, // orange-50 / orange-600 / orange-200
            'Tier 2': { bg: '#FFFBEB', text: '#D97706', border: '#FDE68A' }, // amber-50 / amber-600 / amber-200
            'Tier 3': { bg: '#FEFCE8', text: '#CA8A04', border: '#FEF08A' }, // yellow-50 / yellow-600 / yellow-200
            'Won':   { bg: '#F0FDF4', text: '#16A34A', border: '#BBF7D0' }, // green-50 / green-600 / green-200
            'Lost':  { bg: '#F9FAFB', text: '#4B5563', border: '#E5E7EB' }, // gray-50 / gray-600 / gray-200
        };

        const key = Object.keys(colorMap).find(
            k => k.toLowerCase() === title.toLowerCase()
        );

        if (key) {
            const { bg, text, border } = colorMap[key];

            return {
                backgroundColor: bg,
                dotColor: border,
                color: text,
                border: withBorder ? `1px solid ${border}` : 'inherit',
            };
        }

        const index = statuses.findIndex(
            s => s.toLowerCase() === title.toLowerCase()
        );

        if (index !== -1 && index < palettes.length) {
            return {
                backgroundColor: theme.colors[palettes[index]][50],
                dotColor: theme.colors[palettes[index]][400],
                color: theme.colors[palettes[index]][700],
                border: withBorder
                    ? `1px solid ${theme.colors[palettes[index]][200]} !important`
                    : 'inherit',
            };
        }

        return {
            backgroundColor: theme.colors.neutral[100],
            dotColor: theme.colors.neutral[400],
            color: theme.colors.neutral[600],
            border: withBorder
            ? `1px solid ${theme.colors.neutral[500]} !important`
            : 'inherit',
        };
    }, [statuses, palettes]);

    const handleChange = useCallback(async (newStatus: string) => {
        const { tagGroups, status, ...props } = dashboard;

        setSelected(newStatus);
        setAnchorEl(null);
        await updateDashboardFunc({
            ...props,
            status: newStatus === dashboard.status ? '<rename this status>' : newStatus,
            tagGroups: tagGroups?.map(({title, tags}) => ({title, tags})) ?? [],
        });
    }, [dashboard]);

    useEffect(() => {
        setSelected(dashboard.status);
    }, [dashboard.status]);

    return (
        <Stack direction="column" spacing={1} width="fit-content">
            <Button variant="outlined"
                className={classes.statusButton}
                onClick={readOnly ? undefined : (e) => setAnchorEl(e.currentTarget)}
                sx={{ ...getColors(selected, true), ...(readOnly && { cursor: 'default' }) }} ref={buttonRef}
                disableRipple={readOnly}>
                <Typography className={classes.statusText}>
                    {selected || 'N/A'}
                </Typography>
                {!readOnly && (
                    <KeyboardArrowDownIcon sx={{ width: 16, height: 16, marginLeft: 'auto' }} />
                )}
            </Button>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    className: classes.popoverPaper,
                }}>
                <Stack direction="row" alignItems="flex-start" width={440}>
                    <Box className={classes.leftSection}>
                        <Typography className={classes.sectionTitle}>
                            {'Change Status'}
                        </Typography>
                        {statuses.map((item, i) => (
                            <Box className={classes.statusOption}
                                key={'status-option-' + i}
                                onClick={() => handleChange(item)}>
                                <Box className={classes.statusOptionLeft}>
                                    <CircleIcon sx={{ width: 12, height: 12, color: getColors(item)?.dotColor || '#000' }} />
                                    <Typography className={classes.statusOptionText}>
                                        {item}
                                    </Typography>
                                </Box>
                                {item === selected && (
                                    <Typography className={classes.currentBadge}>
                                        Current
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                    <Box className={classes.rightSection}>
                        <Typography className={classes.sectionTitle}>
                            {'Status History'}
                        </Typography>
                        {statusHistory.length > 0 ? (
                            statusHistory.map((historyItem, i) => (
                                <Box key={'history-' + i} className={classes.historyItem}>
                                    <CircleIcon sx={{
                                        width: 8,
                                        height: 8,
                                        marginTop: '5px',
                                        color: getColors(historyItem.status).dotColor
                                    }} />
                                    <Box className={classes.historyContent}>
                                        <Typography className={classes.historyStatus}>
                                            {historyItem.status}
                                        </Typography>
                                        <Typography className={classes.historyTimestamp}>
                                            {moment(historyItem.timestamp).fromNow()}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))
                        ) : (
                            <Typography className={classes.historyTimestamp} sx={{ paddingLeft: '8px' }}>
                                {'No history available'}
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </Popover>
        </Stack>
    );
}

export default DashboardStatusMenu;